import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Users, Calendar, Utensils, Award, ChevronLeft, ChevronRight, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MARBLE_BG = "https://customer-assets.emergentagent.com/job_ddc0959d-af9d-41ba-b3e9-bd108607894c/artifacts/terwkr30_wallpaper-blue-sapphire-luxurious-marbled-texture-background.jpg";

const iconMap = {
  Users, Calendar, Utensils, Award
};

// Text animation variants for smooth enter/exit
const textVariants = {
  enter: {
    opacity: 0,
    y: 30,
  },
  center: {
    opacity: 1,
    y: 0,
  },
  exit: {
    opacity: 0,
    y: -30,
  },
};

export default function HomePage() {
  const [settings, setSettings] = useState(null);
  const [banners, setBanners] = useState([]);
  const [stats, setStats] = useState([]);
  const [sections, setSections] = useState({});
  const [features, setFeatures] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const preloadedImages = useRef({});

  useEffect(() => {
    document.title = 'Club JCI Ltd - Home';
    
    const fetchData = async () => {
      try {
        const [settingsRes, bannersRes, statsRes, sectionsRes, featuresRes, amenitiesRes, galleryRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/banners`),
          axios.get(`${API}/homepage-stats`),
          axios.get(`${API}/homepage-sections`),
          axios.get(`${API}/feature-items`),
          axios.get(`${API}/amenities`),
          axios.get(`${API}/gallery`)
        ]);
        setSettings(settingsRes.data);
        setBanners(bannersRes.data.filter(b => b.is_active));
        setStats(statsRes.data);
        
        const sectionsMap = {};
        sectionsRes.data.forEach(s => { sectionsMap[s.section_key] = s; });
        setSections(sectionsMap);
        
        setFeatures(featuresRes.data);
        setAmenities(amenitiesRes.data.filter(a => !a.is_upcoming).slice(0, 3));
        setGallery(galleryRes.data.slice(0, 8));
        
        if (amenitiesRes.data.length === 0) {
          await axios.post(`${API}/seed-amenities`);
          const newAmenities = await axios.get(`${API}/amenities`);
          setAmenities(newAmenities.data.filter(a => !a.is_upcoming).slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Auto-slide banners
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentBanner(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  const nextBanner = useCallback(() => {
    setCurrentBanner(prev => (prev + 1) % banners.length);
  }, [banners.length]);

  const prevBanner = useCallback(() => {
    setCurrentBanner(prev => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api/images/') 
      ? `${process.env.REACT_APP_BACKEND_URL}${url}/data` 
      : url;
  };

  // Preload all banner images for smooth transitions
  useEffect(() => {
    if (banners.length === 0) return;
    
    banners.forEach((banner, index) => {
      const imgUrl = getImageUrl(banner.image_url);
      if (imgUrl && !preloadedImages.current[imgUrl]) {
        const img = new Image();
        img.onload = () => {
          preloadedImages.current[imgUrl] = true;
          setImagesLoaded(prev => ({ ...prev, [index]: true }));
        };
        img.src = imgUrl;
      }
    });
  }, [banners]);

  // Gallery lightbox navigation
  const openLightbox = (index) => setSelectedImageIndex(index);
  const closeLightbox = () => setSelectedImageIndex(null);
  const nextImage = () => setSelectedImageIndex(prev => (prev + 1) % gallery.length);
  const prevImage = () => setSelectedImageIndex(prev => (prev - 1 + gallery.length) % gallery.length);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImageIndex === null) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImageIndex, gallery.length]);

  return (
    <div data-testid="home-page">
      {/* Hero Section with Smooth Animated Banners */}
      {settings?.show_hero_section !== false && (
      <section className="min-h-screen relative flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Preloaded Background Banners - Crossfade Style */}
        <div className="absolute inset-0">
          {banners.length > 0 ? (
            banners.map((banner, index) => (
              <div
                key={banner.id || index}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img
                  src={getImageUrl(banner.image_url)}
                  alt={banner.title}
                  className="w-full h-full object-cover"
                  loading={index === 0 ? "eager" : "lazy"}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#051025] via-[#051025]/70 to-[#051025]/40"></div>
              </div>
            ))
          ) : (
            <div className="absolute inset-0">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-50"
                style={{ backgroundImage: `url(${MARBLE_BG})` }}
              ></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#051025] via-[#051025]/60 to-transparent"></div>
            </div>
          )}
        </div>
        
        {/* Content - No Logo - with smooth text animations */}
        <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentBanner}
              variants={textVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                duration: 0.5,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="banner-content"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#F1F5F9] mb-6">
                {banners.length > 0 && banners[currentBanner]?.title 
                  ? banners[currentBanner].title 
                  : settings?.hero_title || 'Where Visionaries Unite'}
              </h1>
              
              <p className="text-base lg:text-lg text-[#94A3B8] mb-10 max-w-2xl mx-auto font-light">
                {banners.length > 0 && banners[currentBanner]?.subtitle
                  ? banners[currentBanner].subtitle
                  : settings?.hero_subtitle || 'An exclusive sanctuary for entrepreneurs, leaders, and changemakers'}
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {banners.length > 0 && banners[currentBanner]?.button_text ? (
                  <Link
                    to={banners[currentBanner].button_link || '/membership'}
                    className="btn-gold-filled text-sm py-4 px-8 rounded-sm flex items-center gap-2"
                  >
                    {banners[currentBanner].button_text}
                    <ArrowRight size={16} />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/membership"
                      data-testid="hero-membership-cta"
                      className="btn-gold-filled text-sm py-4 px-8 rounded-sm flex items-center gap-2"
                    >
                      Explore Membership
                      <ArrowRight size={16} />
                    </Link>
                    <Link
                      to="/about"
                      data-testid="hero-about-cta"
                      className="btn-gold text-sm py-4 px-8 rounded-sm"
                    >
                      Our Story
                    </Link>
                  </>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Banner Navigation */}
        {banners.length > 1 && (
          <>
            <button
              onClick={prevBanner}
              className="lightbox-nav prev z-30"
              data-testid="banner-prev"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextBanner}
              className="lightbox-nav next z-30"
              data-testid="banner-next"
            >
              <ChevronRight size={24} />
            </button>
            
            {/* Dots */}
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 flex gap-3">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBanner(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentBanner 
                      ? 'bg-[#D4AF37] w-8' 
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                  data-testid={`banner-dot-${index}`}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
        >
          <div className="w-6 h-10 border border-[#D4AF37]/30 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-2 bg-[#D4AF37] rounded-full"
            />
          </div>
        </motion.div>
      </section>
      )}

      {/* Stats Section */}
      {settings?.show_stats_section !== false && stats.length > 0 && (
      <section className="py-16 marble-overlay bg-[#0A192F]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="stat-item text-center"
              >
                <div className="text-3xl md:text-5xl font-bold text-[#D4AF37] mb-2">{stat.number}</div>
                <div className="text-xs md:text-sm text-[#94A3B8] uppercase tracking-widest">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Features Section */}
      {settings?.show_features_section !== false && features.length > 0 && (
      <section className="section-padding marble-overlay" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl text-[#F1F5F9] mb-4">
              {sections.features?.title?.split(' ').map((word, i, arr) => 
                i === arr.length - 1 ? <span key={i} className="gold-text">{word}</span> : word + ' '
              ) || <>The Club JCI <span className="gold-text">Experience</span></>}
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              {sections.features?.subtitle || 'More than a club—a community built on relationships, credibility, and shared growth.'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon] || Users;
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-card p-8 rounded-sm text-center"
                >
                  <IconComponent className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                  <h3 className="text-lg text-[#F1F5F9] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#94A3B8]">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Amenities Preview */}
      {settings?.show_amenities_section !== false && amenities.length > 0 && (
        <section className="section-padding bg-[#0A192F] marble-overlay" data-testid="amenities-preview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row items-end justify-between mb-12"
            >
              <div>
                <h2 className="text-3xl md:text-4xl text-[#F1F5F9] mb-4">
                  {sections.amenities_preview?.title?.split(' ').map((word, i, arr) => 
                    i === arr.length - 1 ? <span key={i} className="gold-text">{word}</span> : word + ' '
                  ) || <>World-Class <span className="gold-text">Amenities</span></>}
                </h2>
                <p className="text-[#94A3B8] max-w-xl">
                  {sections.amenities_preview?.subtitle || 'Experience the finest facilities curated for discerning professionals.'}
                </p>
              </div>
              <Link
                to="/amenities"
                data-testid="view-all-amenities"
                className="mt-6 md:mt-0 text-[#D4AF37] text-sm flex items-center gap-2 hover:gap-3 transition-all"
              >
                View All Amenities
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {amenities.map((amenity, index) => (
                <motion.div
                  key={amenity.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-card rounded-sm overflow-hidden group"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={getImageUrl(amenity.image_url)}
                      alt={amenity.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg text-[#F1F5F9] mb-2">{amenity.name}</h3>
                    <p className="text-sm text-[#94A3B8] line-clamp-2">{amenity.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Preview - Through the Lenses */}
      {settings?.show_gallery_section !== false && gallery.length > 0 && (
        <section className="section-padding" data-testid="gallery-preview">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col md:flex-row items-end justify-between mb-12"
            >
              <div>
                <h2 className="text-3xl md:text-4xl text-[#F1F5F9] mb-4">
                  {sections.gallery_preview?.title ? (
                    <>
                      {sections.gallery_preview.title.split(' ').slice(0, -1).join(' ')}{' '}
                      <span className="gold-text">{sections.gallery_preview.title.split(' ').slice(-1)}</span>
                    </>
                  ) : (
                    <>Through the <span className="gold-text">Lenses</span></>
                  )}
                </h2>
                <p className="text-[#94A3B8] max-w-xl">
                  {sections.gallery_preview?.subtitle || 'Capturing moments of excellence and camaraderie.'}
                </p>
              </div>
              <Link
                to="/gallery"
                data-testid="view-all-gallery"
                className="mt-6 md:mt-0 text-[#D4AF37] text-sm flex items-center gap-2 hover:gap-3 transition-all"
              >
                View Full Gallery
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="aspect-square rounded-sm overflow-hidden group cursor-pointer"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-preview-item-${index}`}
                >
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Lightbox with Navigation */}
      <AnimatePresence>
        {selectedImageIndex !== null && gallery[selectedImageIndex] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 transition-colors z-20"
              onClick={closeLightbox}
            >
              <X size={32} />
            </button>

            {/* Previous button */}
            <button
              className="lightbox-nav prev"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Image */}
            <motion.div
              key={selectedImageIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[85vh] px-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={getImageUrl(gallery[selectedImageIndex].image_url)}
                alt={gallery[selectedImageIndex].title}
                className="max-w-full max-h-[75vh] object-contain rounded-sm mx-auto"
              />
              <div className="text-center mt-4">
                <p className="text-white text-lg">{gallery[selectedImageIndex].title}</p>
                <p className="text-[#94A3B8] text-sm mt-1">
                  {selectedImageIndex + 1} / {gallery.length}
                </p>
              </div>
            </motion.div>

            {/* Next button */}
            <button
              className="lightbox-nav next"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              data-testid="lightbox-next"
            >
              <ChevronRight size={24} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      {settings?.show_cta_section !== false && (
      <section className="section-padding relative marble-bg" data-testid="cta-section">
        <div className="absolute inset-0 bg-[#051025]/85"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl text-[#F1F5F9] mb-6">
              {sections.cta?.title ? (
                <>
                  {sections.cta.title.split(' ').slice(0, -1).join(' ')}{' '}
                  <span className="gold-text">{sections.cta.title.split(' ').slice(-1)}</span>
                </>
              ) : (
                <>Ready to Join the <span className="gold-text">Elite Circle?</span></>
              )}
            </h2>
            <p className="text-[#94A3B8] mb-10 max-w-2xl mx-auto">
              {sections.cta?.subtitle || "Become part of Dhaka's most prestigious professional community. Apply for membership today and unlock a world of opportunities."}
            </p>
            <Link
              to="/contact"
              data-testid="cta-apply"
              className="btn-gold-filled text-sm py-4 px-10 rounded-sm inline-flex items-center gap-2"
            >
              Apply for Membership
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
      )}
    </div>
  );
}
